import React from 'react';
import { Card, Avatar, Typography, Space, Divider } from 'antd';
import {
  HeartOutlined,
  MessageOutlined,
  SendOutlined,
  BookOutlined,
  LikeOutlined,
  ShareAltOutlined,
  InstagramOutlined,
  FacebookOutlined,
} from '@ant-design/icons';
import type { SnsPost, SnsPostFile, Platform } from '../api/types';
import { snsPostFilesApi } from '../api/endpoints';
import { PLATFORM_COLORS } from '../constants/status';

const { Text, Paragraph } = Typography;

interface PostPreviewProps {
  post: SnsPost;
}

const InstagramPreview: React.FC<PostPreviewProps> = ({ post }) => {
  const firstPhoto = post.sns_post_files?.[0];
  return (
    <div style={{ maxWidth: 400, border: '1px solid #dbdbdb', borderRadius: 8, background: '#fff', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', padding: '10px 12px', gap: 10 }}>
        <Avatar size={32} style={{ background: PLATFORM_COLORS.instagram }}>
          <InstagramOutlined />
        </Avatar>
        <Text strong style={{ fontSize: 13 }}>{post.store?.name || 'store_name'}</Text>
      </div>
      {/* Image */}
      <div style={{ width: '100%', aspectRatio: '1', background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
        {firstPhoto ? (
          <img
            src={snsPostFilesApi.thumbnail(firstPhoto.id)}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <Text type="secondary">写真を追加するとここに表示されます</Text>
        )}
      </div>
      {/* Actions */}
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 12px' }}>
        <Space size={16}>
          <HeartOutlined style={{ fontSize: 22 }} />
          <MessageOutlined style={{ fontSize: 22 }} />
          <SendOutlined style={{ fontSize: 22 }} />
        </Space>
        <BookOutlined style={{ fontSize: 22 }} />
      </div>
      {/* Caption */}
      <div style={{ padding: '0 12px 12px' }}>
        <div>
          <Text strong style={{ fontSize: 13 }}>{post.store?.name || 'store_name'}</Text>{' '}
          <Text style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{post.caption || '原稿が未入力です'}</Text>
        </div>
      </div>
    </div>
  );
};

const FacebookPreview: React.FC<PostPreviewProps> = ({ post }) => {
  const firstPhoto = post.sns_post_files?.[0];
  return (
    <div style={{ maxWidth: 500, border: '1px solid #dddfe2', borderRadius: 8, background: '#fff', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', padding: 12, gap: 10 }}>
        <Avatar size={40} style={{ background: PLATFORM_COLORS.facebook }}>
          <FacebookOutlined />
        </Avatar>
        <div>
          <Text strong style={{ fontSize: 14, display: 'block' }}>{post.store?.name || 'Store Name'}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{post.scheduled_date} ・ 🌐</Text>
        </div>
      </div>
      {/* Caption */}
      <div style={{ padding: '0 12px 8px' }}>
        <Paragraph style={{ fontSize: 14, margin: 0, whiteSpace: 'pre-wrap' }}>
          {post.caption || '原稿が未入力です'}
        </Paragraph>
      </div>
      {/* Image */}
      {firstPhoto && (
        <div style={{ width: '100%', maxHeight: 400, overflow: 'hidden' }}>
          <img
            src={snsPostFilesApi.thumbnail(firstPhoto.id)}
            alt=""
            style={{ width: '100%', objectFit: 'cover' }}
          />
        </div>
      )}
      {/* Actions */}
      <Divider style={{ margin: 0 }} />
      <div style={{ display: 'flex', justifyContent: 'space-around', padding: '8px 0' }}>
        <Space><LikeOutlined /> <Text type="secondary">いいね！</Text></Space>
        <Space><MessageOutlined /> <Text type="secondary">コメント</Text></Space>
        <Space><ShareAltOutlined /> <Text type="secondary">シェア</Text></Space>
      </div>
    </div>
  );
};

const LinePreview: React.FC<PostPreviewProps> = ({ post }) => {
  const firstPhoto = post.sns_post_files?.[0];
  return (
    <div style={{ maxWidth: 360, background: '#7494C0', borderRadius: 12, padding: 16, minHeight: 200 }}>
      {/* Chat bubble */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        <Avatar size={36} style={{ background: PLATFORM_COLORS.line, flexShrink: 0 }}>
          {(post.store?.name || 'S')[0]}
        </Avatar>
        <div>
          <Text style={{ fontSize: 12, color: '#fff', display: 'block', marginBottom: 4 }}>
            {post.store?.name || 'Store'}
          </Text>
          <div style={{ background: '#fff', borderRadius: '0 16px 16px 16px', padding: 12, maxWidth: 260 }}>
            {firstPhoto && (
              <img
                src={snsPostFilesApi.thumbnail(firstPhoto.id)}
                alt=""
                style={{ width: '100%', borderRadius: 8, marginBottom: 8 }}
              />
            )}
            <Text style={{ fontSize: 14, whiteSpace: 'pre-wrap' }}>
              {post.caption || '原稿が未入力です'}
            </Text>
          </div>
        </div>
      </div>
    </div>
  );
};

const PostPreview: React.FC<PostPreviewProps> = ({ post }) => {
  const platform = post.platform as Platform;

  switch (platform) {
    case 'instagram':
      return <InstagramPreview post={post} />;
    case 'facebook':
      return <FacebookPreview post={post} />;
    case 'line':
      return <LinePreview post={post} />;
    default:
      return <Text type="secondary">プレビューに対応していないプラットフォームです</Text>;
  }
};

export default PostPreview;
